/**
 * TraineesTab — searchable trainee table with CRUD, batch filter, and
 * XLSX/CSV import + export.
 *
 * The "Add Trainee" dialog renders all visible fields dynamically based on
 * the current org's FieldDefinition list.
 */
import { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Plus, Search, Upload, Download, Trash2, Pencil, X, BarChart3, GitMerge } from 'lucide-react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { useAuth } from '../../contexts/AuthContext';
import { ImportPreviewDialog } from './ImportPreviewDialog';
import { DuplicatesDialog } from './DuplicatesDialog';
import { ExportTraineesDialog } from './ExportTraineesDialog';
import api from '../../api/client';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Textarea } from '../../components/ui/textarea';
import TraineeDashboardDialog from './TraineeDashboardDialog';
import { TraineeFormDialog, renderFieldValue } from './TraineeFormDialog';
import { AttemptedChip } from './AttemptedChip';
import { TraineeFilterBar, useTraineeFilter, ALL } from './TraineeFilterContext';

const ALL_BATCHES = '__all__';
const NO_BATCH = '__none__';

export default function TraineesTab() {
  const { get, post, put, del } = useApi();
  const { hasRole } = useAuth();
  const canEdit = hasRole('trainer');
  const { batchId, scopeQS, batches, departments } = useTraineeFilter();

  const [trainees, setTrainees] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const [fields, setFields] = useState([]);

  const [search, setSearch] = useState('');

  const [openDialog, setOpenDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', employee_id: '', batch_id: '', fields: {} });
  const [confirmDelete, setConfirmDelete] = useState(null);

  const [importing, setImporting] = useState(false);
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const [dupGroups, setDupGroups] = useState(0);
  const [dupBannerDismissed, setDupBannerDismissed] = useState(
    () => sessionStorage.getItem('lumyn-dup-banner-dismissed') === '1'
  );

  const loadDupCount = useCallback(() => {
    if (!canEdit) return;
    get('/trainees/duplicates/count')
      .then(res => setDupGroups(res.groups || 0))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canEdit]);

  useEffect(() => { loadDupCount(); }, [loadDupCount]);
  const [importResult, setImportResult] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const fileInputRef = useRef(null);
  const [dashboardFor, setDashboardFor] = useState(null);
  const [dashboardFocus, setDashboardFocus] = useState(null);
  const [coverage, setCoverage] = useState({});

  useEffect(() => {
    if (trainees.length === 0) { setCoverage({}); return; }
    post('/trainees/assessment-coverage',
         { trainee_ids: trainees.map(t => t.trainee_id), batch_id: batchId && batchId !== ALL ? batchId : null })
      .then(r => setCoverage(r.coverage || {})).catch(() => setCoverage({}));
  }, [trainees, batchId, post]);

  const openAssessmentHistory = (t) => { setDashboardFocus('assessments'); setDashboardFor(t); };

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);
      const scope = scopeQS();
      if (scope) { const [k, v] = scope.split('='); params.append(k, decodeURIComponent(v)); }
      params.append('limit', '200');
      const res = await get(`/trainees?${params.toString()}`);
      setTrainees(res.trainees || []);
      setTotal(res.total || 0);
    } catch { toast.error('Failed to load trainees'); }
    finally { setLoading(false); }
  }, [get, search, scopeQS]);

  const loadMeta = useCallback(async () => {
    try {
      const f = await get('/trainees/fields');
      setFields(f.fields || []);
    } catch { /* surfaced via main load */ }
  }, [get]);

  useEffect(() => { loadMeta(); }, [loadMeta]);
  useEffect(() => {
    const t = setTimeout(loadList, 200);
    return () => clearTimeout(t);
  }, [loadList]);

  const visibleFields = useMemo(
    () => fields.filter(f => f.is_visible && f.field_type !== 'calculated'),
    [fields]
  );
  const summaryFields = useMemo(
    () => fields.filter(f => f.is_visible && ['training_status', 'certification_status', 'alert_status'].includes(f.field_key)),
    [fields]
  );
  const batchNameMap = useMemo(
    () => Object.fromEntries(batches.map(b => [b.batch_id, b.name])),
    [batches]
  );

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', email: '', employee_id: '', batch_id: '', fields: {} });
    setOpenDialog(true);
  };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name || '', email: t.email || '', employee_id: t.employee_id || '',
      batch_id: t.batch_id || '', fields: { ...(t.fields || {}) },
    });
    setOpenDialog(true);
  };

  const onSave = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    const payload = {
      name: form.name.trim(),
      email: form.email || null,
      employee_id: form.employee_id || null,
      batch_id: form.batch_id === NO_BATCH || form.batch_id === '' ? null : form.batch_id,
      fields: form.fields || {},
    };
    try {
      if (editing) {
        await put(`/trainees/${editing.trainee_id}`, payload);
        toast.success('Trainee updated');
      } else {
        const created = await post('/trainees', payload);
        toast.success(created?.enrolled_existing ? created.message : 'Trainee added');
      }
      setOpenDialog(false);
      await loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await del(`/trainees/${confirmDelete.trainee_id}`);
      toast.success('Trainee deleted');
      setConfirmDelete(null);
      await loadList();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const onImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/trainees/bulk/import?dry_run=true', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportPreview({ preview: res.data, file });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const onConfirmImport = async () => {
    if (!importPreview) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append('file', importPreview.file);
      const res = await api.post('/trainees/bulk/import', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setImportResult(res.data);
      toast.success(`Imported: ${res.data.created} created, ${res.data.updated} updated`);
      await Promise.all([loadList(), loadMeta()]);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Import failed');
    } finally {
      setImporting(false);
      setImportPreview(null);
    }
  };

  const onExport = async (includeAll = false) => {
    try {
      const res = await api.get(`/trainees/bulk/export?include_all_assessments=${includeAll}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `trainees-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch {
      toast.error('Export failed');
    }
  };

  return (
    <div className="space-y-4" data-testid="trainees-tab">
      {canEdit && dupGroups > 0 && !dupBannerDismissed && !showDuplicates && (
        <div className="flex items-center justify-between gap-3 px-4 py-2.5 border border-amber-600/40 bg-amber-50 rounded-md"
             data-testid="duplicates-banner">
          <p className="text-sm">
            <GitMerge className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            <span className="font-medium">{dupGroups} duplicate trainee group{dupGroups === 1 ? '' : 's'} detected</span>
            <span className="text-muted-foreground"> — same email across multiple records. Merge them so history lives in one place.</span>
          </p>
          <div className="flex items-center gap-1.5 shrink-0">
            <Button size="sm" variant="outline" onClick={() => setShowDuplicates(true)}
                    data-testid="duplicates-banner-review-btn">
              Review &amp; merge
            </Button>
            <Button size="sm" variant="ghost" title="Dismiss for this session"
                    onClick={() => { setDupBannerDismissed(true); sessionStorage.setItem('lumyn-dup-banner-dismissed', '1'); }}
                    data-testid="duplicates-banner-dismiss-btn">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div className="flex flex-wrap items-end gap-3">
          <TraineeFilterBar extra={
            <div className="relative">
              <label className="text-xs font-medium text-muted-foreground block mb-1.5">Search</label>
              <Search className="w-4 h-4 absolute left-2 top-[34px] -translate-y-1/2 text-muted-foreground" />
              <Input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search name, email, Cisco ID…"
                className="pl-8 w-64"
                data-testid="trainee-search"
              />
            </div>
          } />
        </div>
        <div className="flex gap-2">
          <input ref={fileInputRef} type="file" accept=".xlsx,.xls,.csv" hidden
                 onChange={onImport} data-testid="import-file-input" />
          {canEdit && (
            <>
              <Button variant="outline" onClick={() => setShowDuplicates(true)}
                      data-testid="merge-duplicates-btn">
                <GitMerge className="w-4 h-4 mr-2" /> Duplicates
              </Button>
              <Button variant="outline" onClick={() => fileInputRef.current?.click()}
                      disabled={importing} data-testid="import-btn">
                <Upload className="w-4 h-4 mr-2" />
                {importing ? 'Importing…' : 'Import'}
              </Button>
              <Button variant="outline" onClick={() => setShowExport(true)} data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" /> Export
              </Button>
              <Button onClick={openCreate} data-testid="create-trainee-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Trainee
              </Button>
            </>
          )}
        </div>
      </div>

      <Card>
        {loading ? (
          <p className="p-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : trainees.length === 0 ? (
          <div className="p-12 text-center" data-testid="trainees-empty-state">
            <p className="font-display text-lg font-semibold">No trainees yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Add trainees one-by-one or import them from your Google Sheet / Excel.
            </p>
          </div>
        ) : (
          <Table data-testid="trainees-table">
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Cisco ID</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead>Assessments</TableHead>
                {summaryFields.map(f => (
                  <TableHead key={f.field_id}>{f.label}</TableHead>
                ))}
                {canEdit && <TableHead className="w-24" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {trainees.map(t => (
                <TableRow key={t.trainee_id} data-testid={`trainee-row-${t.trainee_id}`}>
                  <TableCell className="font-medium">{t.name}</TableCell>
                  <TableCell className="text-sm">{t.employee_id || '—'}</TableCell>
                  <TableCell className="text-sm">{t.email || '—'}</TableCell>
                  <TableCell className="text-sm">
                    {batchNameMap[t.batch_id] || '—'}
                    {(t.batch_ids || []).length > 1 && (
                      <span className="ml-1 text-xs text-muted-foreground"
                            title={t.batch_ids.filter(b => b !== t.batch_id).map(b => batchNameMap[b] || b).join(', ')}
                            data-testid={`trainee-multi-batch-${t.trainee_id}`}>
                        +{t.batch_ids.length - 1}
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    <AttemptedChip coverage={coverage[t.trainee_id]} onClick={() => openAssessmentHistory(t)}
                                   testId={`attempted-chip-${t.trainee_id}`} />
                  </TableCell>
                  {summaryFields.map(f => (
                    <TableCell key={f.field_id} className="text-sm">
                      {renderFieldValue(f, (t.fields || {})[f.field_key])}
                    </TableCell>
                  ))}
                  {canEdit && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" onClick={() => setDashboardFor(t)}
                              data-testid={`view-trainee-${t.trainee_id}`} title="View dashboard">
                        <BarChart3 className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => openEdit(t)}
                              data-testid={`edit-trainee-${t.trainee_id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setConfirmDelete(t)}
                              data-testid={`delete-trainee-${t.trainee_id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>

      {total > 0 && (
        <p className="text-xs text-muted-foreground">Showing {trainees.length} of {total} trainees</p>
      )}

      <ExportTraineesDialog open={showExport} onOpenChange={setShowExport} onExport={onExport} />
      <TraineeFormDialog
        open={openDialog} onOpenChange={setOpenDialog}
        editing={editing} form={form} setForm={setForm}
        batches={batches} departments={departments} fields={fields} onSave={onSave}
      />

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete trainee?</AlertDialogTitle>
            <AlertDialogDescription>
              Removes <strong>{confirmDelete?.name}</strong> permanently.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="confirm-delete-trainee-btn">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {importPreview && (
        <ImportPreviewDialog
          preview={importPreview.preview}
          fileName={importPreview.file.name}
          importing={importing}
          onConfirm={onConfirmImport}
          onCancel={() => setImportPreview(null)}
        />
      )}
      <Dialog open={!!importResult} onOpenChange={(o) => !o && setImportResult(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Import Summary</DialogTitle></DialogHeader>
          {importResult && (
            <div className="space-y-2 text-sm">
              <div className="grid grid-cols-3 gap-3">
                <Card className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Created</p>
                  <p className="font-display tabular-nums text-2xl font-bold">{importResult.created}</p>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Updated</p>
                  <p className="font-display tabular-nums text-2xl font-bold">{importResult.updated}</p>
                </Card>
                <Card className="p-3 text-center">
                  <p className="text-xs text-muted-foreground">Skipped</p>
                  <p className="font-display tabular-nums text-2xl font-bold">{importResult.skipped}</p>
                </Card>
              </div>
              {importResult.errors?.length > 0 && (
                <div className="max-h-48 overflow-y-auto border rounded p-2 text-xs">
                  {importResult.errors.map((e, i) => (
                    <p key={i} className="text-rose-600">Row {e.row}: {e.message}</p>
                  ))}
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button onClick={() => setImportResult(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <TraineeDashboardDialog
        trainee={dashboardFor}
        focus={dashboardFocus}
        onClose={() => { setDashboardFor(null); setDashboardFocus(null); }}
        onChanged={loadList}
      />

      {showDuplicates && (
        <DuplicatesDialog onClose={() => { setShowDuplicates(false); loadList(); loadDupCount(); }} />
      )}
    </div>
  );
}
